'use client';

import { Alert, Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { handleChange } from '../handleOnChange';
import Recaptcha from '../Recaptcha';
import { useStyles } from '../style';
import { validateFields } from '../validate';
import AccountDetails from './AccountDetails';
import { ACTIONS, SWEEPR_WIDGET_ERROR } from '../../../util/constants';
import HandleError from '../../shared/widget-error-handling/HandleError';

const { ERROR_TRY_AGAIN, ERROR_CLOSE } = ACTIONS;
const { TITLE, DESCRIPTION, TRY_AGAIN, CLOSE, TYPE } = SWEEPR_WIDGET_ERROR;
/**
 * @name AccountVerificationContainer
 * @description Account verification fields container
 * @param {*} props - React props
 * @returns {JSX.Element} Account Verification Container
 */
const AccountVerificationContainer = props => {
  const theme = useTheme();
  const style = useStyles(theme);
  const fieldsRef = useRef({});
  const recaptchaRef = useRef(null);
  const [errors, setErrors] = useState({
    accountNumber: null,
    landLineNumber: null,
  });
  const {
    setValues,
    setDisableCTA,
    values,
    setCaptchaV2,
    apiError,
    enableCaptchaV2,
    captchaV2,
    errorCount,
    maxErrorCount,
  } = props;

  /**
   * @name onChange - Function to get user input values
   * @param {Function} event - Event can be something the browser does, or something a user does
   */
  const onChange = event => {
    const { name, value } = event.target;
    handleChange(name, value, setErrors, setValues);
  };

  useEffect(() => {
    validateFields(errors, values, setDisableCTA);
  }, [errors, values?.accountNumber, values?.landLineNumber, values, setDisableCTA]);

  useEffect(() => {
    if (enableCaptchaV2.account) {
      if (captchaV2?.account) {
        setDisableCTA(false);
      } else {
        setDisableCTA(true);
      }
    }
  }, [enableCaptchaV2?.account, captchaV2?.account, setDisableCTA]);

  return (
    <>
      {errorCount === 0 && (
        <>
          <AccountDetails
            ref={fieldsRef}
            errors={{
              accountNumber: errors.accountNumber,
              landLineNumber: errors.landLineNumber,
            }}
            values={{
              accountNumber: values?.accountNumber,
              landLineNumber: values?.landLineNumber,
            }}
            onChange={onChange}
            onBlur={onChange}
          />
          {enableCaptchaV2?.account && !apiError && (
            <Recaptcha
              recaptchaRef={recaptchaRef}
              setCaptchaV2={setCaptchaV2}
              setDisableCTA={setDisableCTA}
            />
          )}
          {apiError?.invalidAccount && (
            <Box>
              <Alert severity='error' sx={style.alert}>
                {apiError?.errorMessage}
              </Alert>
            </Box>
          )}
        </>
      )}
      {errorCount === 1 && (
        <HandleError
          title={TITLE}
          description={DESCRIPTION}
          cta={[{ type: TYPE, name: TRY_AGAIN, action: ERROR_TRY_AGAIN }]}
          handleAction={props.handleAction}
        />
      )}
      {errorCount === maxErrorCount && (
        <HandleError
          title={TITLE}
          description={DESCRIPTION}
          cta={[{ type: TYPE, name: CLOSE, action: ERROR_CLOSE }]}
          handleAction={props.handleAction}
        />
      )}
    </>
  );
};

AccountVerificationContainer.displayName = 'AccountVerificationContainer';
export default AccountVerificationContainer;
